import { useEffect, useRef } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Spinner } from "./ui/spinner";
import useUserAgent from "@/lib/hooks/useUserAgent";
import {
  NOTIF_TOAST_DISMISSED_AT_KEY,
  NOTIF_TOAST_DISMISS_TTL_MS,
  NOTIF_BLOCKED_TOAST_DISMISSED_AT_KEY,
} from "@/lib/constants";
import { useAtomValue } from "jotai";
import { isAnyOverlayOpenAtom } from "@/lib/overlayAtoms";
import { usePushNotifications } from "@/lib/hooks/usePushNotifications";
import { usePrivy } from "@privy-io/react-auth";
import { useStats } from "@/lib/hooks/useApi";
import { DefaultButton } from "./DefaultButton";

export default function NotifToasterTrigger() {
  const { user } = usePrivy();
  const { data: statsData } = useStats(user?.wallet?.address ?? "");
  const userAddress = statsData?.embeddedWallet;
  const { state, enable } = usePushNotifications({ userAddress });
  const { isStandalone } = useUserAgent();
  const isAnyOverlayOpen = useAtomValue(isAnyOverlayOpenAtom);

  // Refs to debounce/delay showing toasts
  const permissionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const blockedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Helper to read if dismissal TTL has expired
  const isSuppressed = () => {
    if (typeof window === "undefined") return false;
    try {
      const raw = window.localStorage.getItem(NOTIF_TOAST_DISMISSED_AT_KEY);
      if (!raw) return false;
      const ts = Number(raw);
      if (Number.isNaN(ts)) return false;
      return Date.now() - ts < NOTIF_TOAST_DISMISS_TTL_MS;
    } catch {
      return false;
    }
  };

  const setDismissedNow = () => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        NOTIF_TOAST_DISMISSED_AT_KEY,
        String(Date.now()),
      );
    } catch {
      // ignore storage errors
    }
  };

  // Separate suppression for blocked-permission toast (same TTL)
  const isBlockedSuppressed = () => {
    if (typeof window === "undefined") return false;
    try {
      const raw = window.localStorage.getItem(
        NOTIF_BLOCKED_TOAST_DISMISSED_AT_KEY,
      );
      if (!raw) return false;
      const ts = Number(raw);
      if (Number.isNaN(ts)) return false;
      return Date.now() - ts < NOTIF_TOAST_DISMISS_TTL_MS;
    } catch {
      return false;
    }
  };

  const setBlockedDismissedNow = () => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        NOTIF_BLOCKED_TOAST_DISMISSED_AT_KEY,
        String(Date.now()),
      );
    } catch {
      // ignore storage errors
    }
  };

  // Show toast only when user is promptable/loading and not suppressed
  useEffect(() => {
    // Clear any pending timer before scheduling
    if (permissionTimerRef.current) {
      clearTimeout(permissionTimerRef.current);
      permissionTimerRef.current = null;
    }

    if (!userAddress) return;
    if (isStandalone !== true) return;
    if (state !== "promptable" && state !== "loading") return;
    if (isSuppressed()) return;
    if (isAnyOverlayOpen) return; // wait until dialogs close

    // Slight delay to avoid overlap with closing animations
    permissionTimerRef.current = setTimeout(() => {
      toast.custom(
        () => (
          <div className="relative p-3 flex items-start gap-3">
            <img
              src="https://cdn.navalabs.io/icons/valentino-head_fh2q2k.png"
              alt="Varentino"
              className="rounded-md w-14 shadow-sm mt-4"
            />
            <div className="flex-1 text-white">
              <p className="font-extrabold text-shadow-black-sm text-base uppercase">
                Enable Notifications?
              </p>
              <p className="font-bold text-xs text-emerald-50">
                Be the first to know when a new tournament opens for a chance to
                win 25x your $. Don't miss out!
              </p>

              <div className="flex w-full justify-end items-center gap-4 mt-2">
                <Button
                  variant="link"
                  className="text-sm font-bold text-zinc-300 text-nowrap p-0 hover:no-underline"
                  aria-label="Dismiss notification prompt"
                  onClick={() => {
                    setDismissedNow();
                    toast.dismiss("toast:notification-permission");
                  }}
                >
                  Ignore
                </Button>

                <DefaultButton
                  variant="green"
                  className="h-8 pt-0! text-sm font-bold text-nowrap hover:no-underline"
                  onClick={async () => {
                    await enable();
                    toast.dismiss("toast:notification-permission");
                  }}
                  disabled={state === "loading"}
                >
                  {state === "loading" ? (
                    <Spinner />
                  ) : (
                    <span className="-mt-1">OK, Let's Go!</span>
                  )}
                </DefaultButton>
              </div>
            </div>

            <button
              className="absolute top-2 right-2 hover:cursor-pointer"
              aria-label="Dismiss notification prompt"
              onClick={() => {
                setDismissedNow();
                toast.dismiss("toast:notification-permission");
              }}
            >
              <X className="size-4 text-zinc-400" />
            </button>
          </div>
        ),
        {
          id: "toast:notification-permission",
          duration: Infinity,
          onDismiss: () => {
            setDismissedNow();
          },
        },
      );
    }, 3000);
  }, [state, isStandalone, isAnyOverlayOpen, userAddress, enable]);

  // Show toast when notifications are blocked/revoked (and not suppressed)
  useEffect(() => {
    if (!userAddress) return;
    if (isStandalone !== true) return;
    if (state !== "blocked") return;
    if (isBlockedSuppressed()) return;
    if (isAnyOverlayOpen) return; // wait until dialogs close

    // Ensure the prompt toast is not visible
    toast.dismiss("toast:notification-permission");

    toast.custom(
      () => (
        <div className="relative p-3 flex items-start gap-3">
          <img
            src="https://cdn.navalabs.io/icons/valentino-head_fh2q2k.png"
            alt="Varentino"
            className="rounded-md w-14 shadow-sm mt-1"
          />
          <div className="flex-1 text-white">
            <p className="font-extrabold text-shadow-black-sm text-base uppercase">
              Notification is Blocked
            </p>
            <p className="font-bold text-xs text-emerald-50">
              You permanently blocked notifications. Enable them in your
              settings to get alerts.
            </p>

            <div className="flex w-full justify-end items-center gap-4 mt-2">
              <Button
                variant="link"
                className="text-sm font-bold text-zinc-300 text-nowrap p-0 hover:no-underline"
                aria-label="Dismiss blocked notification message"
                onClick={() => {
                  setBlockedDismissedNow();
                  toast.dismiss("toast:notification-permission-blocked");
                }}
              >
                Ignore
              </Button>

              <DefaultButton
                className="h-8 pt-0! text-sm font-bold text-nowrap hover:no-underline"
                onClick={() => {
                  setBlockedDismissedNow();
                  toast.dismiss("toast:notification-permission-blocked");
                  try {
                    window.location.href =
                      "https://crypto-fantasy-league-1.gitbook.io/crypto-fantasy-league-docs/miscellaneous/re-enabling-notification";
                  } catch {
                    // ignore navigation errors
                  }
                }}
              >
                <span className="-mt-1">Read the Guide</span>
              </DefaultButton>
            </div>
          </div>

          <button
            className="absolute top-2 right-2 hover:cursor-pointer"
            aria-label="Dismiss blocked notification message"
            onClick={() => {
              setBlockedDismissedNow();
              toast.dismiss("toast:notification-permission-blocked");
            }}
          >
            <X className="size-4 text-zinc-400" />
          </button>
        </div>
      ),
      {
        id: "toast:notification-permission-blocked",
        duration: Infinity,
        onDismiss: () => {
          setBlockedDismissedNow();
        },
      },
    );
  }, [state, isStandalone, userAddress, isAnyOverlayOpen]);

  // Dismiss toast when leaving promptable/loading (enabled/blocked or not standalone)
  useEffect(() => {
    if (isStandalone !== true) {
      toast.dismiss("toast:notification-permission");
      toast.dismiss("toast:notification-permission-blocked");
      return;
    }
    if (state === "enabled" || state === "blocked") {
      toast.dismiss("toast:notification-permission");
    }
  }, [state, isStandalone]);

  // Dismiss blocked toast when leaving blocked state
  useEffect(() => {
    if (isStandalone !== true) {
      toast.dismiss("toast:notification-permission-blocked");
      return;
    }
    if (state !== "blocked") {
      toast.dismiss("toast:notification-permission-blocked");
    }
  }, [state, isStandalone]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      toast.dismiss("toast:notification-permission");
      toast.dismiss("toast:notification-permission-blocked");
      if (permissionTimerRef.current) clearTimeout(permissionTimerRef.current);
      if (blockedTimerRef.current) clearTimeout(blockedTimerRef.current);
    };
  }, []);

  console.log("ToasterTrigger:", state);

  // No local Toaster here; rely on global Toaster in layout
  return null;
}
